import type { User } from "@supabase/supabase-js";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Check } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { PENDING_PLAN_KEY } from "@/lib/plans";
import { visibleTiers as tiers } from "@/content/pricing";

interface PricingSectionProps {
  user?: User | null;
  /** Rendered as an <h1> on the dedicated pricing page. */
  headingLevel?: "h1" | "h2";
}

export function PricingSection({ user, headingLevel = "h2" }: PricingSectionProps) {
  const navigate = useNavigate();
  const { t: tr } = useTranslation();
  const Heading = headingLevel;
  const loading = false;

  /** Client navigation with a hard-navigation fallback if the router is blocked. */
  const go = (path: string, nav: () => void) => {
    try {
      nav();
    } catch {
      window.location.assign(path);
      return;
    }
    window.setTimeout(() => {
      if (!window.location.pathname.startsWith(path.split("?")[0]!)) {
        window.location.assign(path);
      }
    }, 250);
  };

  const handleSubscribe = (tier: (typeof tiers)[number]) => {
    if (!user) {
      // Remember which plan they wanted so checkout resumes after sign-in.
      try {
        sessionStorage.setItem(PENDING_PLAN_KEY, tier.priceId);
      } catch {
        /* storage unavailable — user can just click again */
      }
      go("/auth?mode=signup", () => navigate({ to: "/auth", search: { mode: "signup" } }));
      return;
    }

    go(`/checkout?plan=${tier.priceId}`, () =>
      navigate({ to: "/checkout", search: { plan: tier.priceId } }),
    );
  };

  // Resume a checkout the visitor started before signing in.
  useEffect(() => {
    if (!user) return;
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem(PENDING_PLAN_KEY);
    } catch {
      return;
    }
    if (!pending) return;
    sessionStorage.removeItem(PENDING_PLAN_KEY);
    const tier = tiers.find((t) => t.priceId === pending);
    if (!tier) return;
    navigate({ to: "/checkout", search: { plan: tier.priceId } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <section id="pricing" className="scroll-mt-24 bg-background py-20">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <Heading className="text-3xl text-primary sm:text-4xl">
            {headingLevel === "h1" ? tr("pricing.headingPage") : tr("pricing.heading")}
          </Heading>
          <p className="mt-3 text-muted-foreground">{tr("pricing.intro")}</p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {tiers.map((t) => (
            <article
              key={t.name}
              className={`surface-card relative flex flex-col p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-card)] ${
                t.featured ? "border-accent ring-1 ring-accent" : ""
              }`}
            >
              {t.featured && (
                <span className="absolute -top-3 start-7 rounded-full bg-accent px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-accent-foreground">
                  {tr("pricing.mostPopular")}
                </span>
              )}
              <h3 className="text-xl text-primary">{tr(`pricing.tiers.${t.priceId}.name`, t.name)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {tr(`pricing.tiers.${t.priceId}.lessons`, t.lessons)} ·{" "}
                {tr(`pricing.tiers.${t.priceId}.cadence`, t.cadence)}
              </p>

              <p className="mt-5 flex flex-wrap items-baseline gap-1">
                <span className="font-display text-4xl font-extrabold text-primary">
                  ${t.monthly}
                </span>
                <span className="text-sm text-muted-foreground">{t.oneTime ? tr("pricing.oneTime") : tr("pricing.perMonth")}</span>
              </p>
              <p className="mt-1 text-sm font-semibold text-accent">
                {tr("pricing.perLesson", { amount: t.perLesson })}
              </p>

              {t.oneTime && (
                <p className="mt-4 text-sm text-muted-foreground">{tr("pricing.starterNote")}</p>
              )}
              <ul className="mt-6 flex-1 space-y-2.5 text-sm">
                {(
                  tr(`pricing.tiers.${t.priceId}.features`, {
                    returnObjects: true,
                    defaultValue: t.features,
                  }) as string[]
                ).map((f) => (
                  <li key={f} className="flex gap-2 text-primary">
                    <Check className="mt-0.5 size-4 shrink-0 text-accent" />
                    <span className="min-w-0 break-words">{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant={t.featured ? "hero" : "softOutline"}
                className="mt-7 h-auto w-full whitespace-normal break-words rounded-lg px-4 py-3 text-center leading-snug"
                onClick={() => handleSubscribe(t)}
                disabled={loading}
              >
                {t.oneTime
                  ? tr("pricing.getStarterPack")
                  : user
                    ? tr("pricing.subscribe")
                    : tr("pricing.signInToSubscribe")}
              </Button>

            </article>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          {tr("pricing.noteLessonLength")}
        </p>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          {tr("pricing.noteCohorts")}
        </p>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          {tr("pricing.noteSibling")}
        </p>
      </div>
    </section>
  );
}

/** Condensed homepage teaser linking through to the full /pricing page. */
export function PricingTeaser() {
  const { t: tr } = useTranslation();
  return (
    <section className="bg-background py-20">
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <h2 className="text-3xl text-primary sm:text-4xl">{tr("pricing.heading")}</h2>
          <p className="mt-3 text-muted-foreground">{tr("pricing.introTeaser")}</p>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {tiers.map((t) => (
            <article
              key={t.name}
              className={`surface-card flex flex-col p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-card)] ${
                t.featured ? "border-accent ring-1 ring-accent" : ""
              }`}
            >
              <h3 className="text-lg text-primary">{tr(`pricing.tiers.${t.priceId}.name`, t.name)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {tr(`pricing.tiers.${t.priceId}.lessons`, t.lessons)} ·{" "}
                {tr(`pricing.tiers.${t.priceId}.cadence`, t.cadence)}
              </p>
              <p className="mt-4 flex flex-wrap items-baseline gap-1">
                <span className="font-display text-3xl font-extrabold text-primary">
                  ${t.monthly}
                </span>
                <span className="text-sm text-muted-foreground">{t.oneTime ? tr("pricing.oneTime") : tr("pricing.perMonth")}</span>
              </p>
              <p className="mt-1 text-sm font-semibold text-accent">
                {tr("pricing.perLesson", { amount: t.perLesson })}
              </p>
              {t.oneTime && (
                <p className="mt-3 text-sm text-muted-foreground">{tr("pricing.starterNote")}</p>
              )}
            </article>
          ))}
        </div>

        <div className="mt-10">
          <Link
            to="/pricing"
            className="group inline-flex items-center gap-2 text-base font-semibold text-primary transition-colors hover:text-accent"
          >
            {tr("pricing.seeMore")}
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
}
